import type { PlanningSettingsDto } from '../../../lib/api/v2/planning'

/**
 * The org's reference work day, as served by the backend (`GET /api/planning/settings`).
 *
 * SOLY-PLAN-003 MS-05 — these numbers used to be written here, in the frontend, as a hardcoded
 * 8h..17h list. The backend anchors hourly slots on its own constant, so the two could disagree
 * without anything failing: a slot the backend re-anchored at 7h would have been displayed as 8h by
 * a <select> that did not offer 7h, and re-submitted as 8h. One source, served by the side that
 * owns the rule.
 *
 * Since 2026-08-18 the work day is a PREFILL and a hint, not the list of what can be picked: the
 * start time is typed in an <input type="time">, so « 18h30 » — Élodie's own example — is
 * reachable. The endpoint is unchanged; what changed is that the frontend stopped turning a
 * reference into a rule.
 *
 * It matters beyond today's constant: work hours are going to become configurable per organisation,
 * and per weekday. When they do, only what answers that endpoint changes — nothing here, and no
 * component.
 */
export type PlanningSettings = PlanningSettingsDto

/**
 * Used until the settings land, and if the call fails. Deliberately the SAME values the backend
 * serves today: a fallback that disagreed would produce exactly the silent drift this module
 * exists to remove. It is a resilience measure, never a second source of truth.
 */
export const FALLBACK_PLANNING_SETTINGS: PlanningSettings = {
  workDayStartHour: 7,
  workDayEndHour: 16,
  hoursPerWorkDay: 8,
}

let current: PlanningSettings = FALLBACK_PLANNING_SETTINGS

/** Hydrated once when the planning page boots (next to the holidays). */
export function setPlanningSettings(settings: PlanningSettings): void {
  current = settings
}

export function planningSettings(): PlanningSettings {
  return current
}

/**
 * The step, in minutes, a start time is picked on. Half-hours: Élodie's own example is a RDV « à
 * 18h30 » (2026-08-18), so whole hours never covered the need this feature exists for.
 */
export const START_TIME_STEP_MINUTES = 30

/** The step as the HTML `step` attribute of an <input type="time"> wants it — seconds. */
export const START_TIME_STEP_SECONDS = START_TIME_STEP_MINUTES * 60

/**
 * True when a wall-clock « HH:mm » falls outside the org's reference work day.
 *
 * Drives an INFORMATIVE line under the field, never a refusal: the backend has never forbidden a
 * slot outside working hours (ScheduleEvent.ValidateDuration says so in as many words), and this
 * whole change exists because the frontend had quietly turned the reference day into a rule — the
 * <select> of whole hours 7h→16h simply had no 18h30 to offer.
 *
 * A blank or unparseable time is not « outside »: it has nothing to say yet, and the submit guard
 * is what refuses it.
 */
export function isOutsideWorkDay(time: string): boolean {
  const m = /^(\d{2}):(\d{2})$/.exec(time)
  if (!m) return false
  const minutes = Number(m[1]) * 60 + Number(m[2])
  return minutes < current.workDayStartHour * 60 || minutes >= current.workDayEndHour * 60
}

/** The reference day as « 7h–16h », for that same informative line. */
export function workDayLabel(): string {
  return `${current.workDayStartHour}h–${current.workDayEndHour}h`
}

/** Hours worked in one day of estimate — how « il reste 16 h » becomes « il reste 2 j ». */
export function hoursPerWorkDay(): number {
  return current.hoursPerWorkDay
}
